// Feature flags: la bibliotecaria controla qué salas y juegos están disponibles,
// y en qué plataforma (web de la biblioteca / app móvil).
// La web corre en plataforma "web"; la futura app usará estos mismos flags para "móvil".
// Persistencia: localStorage (se puede mover a la base de datos como los tickets/usuarios).
import { getSession } from "./auth";

// Cuenta de DEMOSTRACIÓN (username de Cognito). Con ella se enseña la plataforma,
// así que ve TODOS los módulos y juegos, ignorando los flags del dispositivo: no
// debe faltarle nada por un apagado que alguien dejó en esa PC.
const DEMO_STUDENT = "student";

export interface Flag { on: boolean; web: boolean; movil: boolean; }
export interface FlagItem { key: string; label: string; kind: "sala" | "juego"; }

export const FLAG_ITEMS: FlagItem[] = [
  { key: "servicios", label: "Servicios a la Ciudadanía", kind: "sala" },
  { key: "elemental", label: "Elemental", kind: "sala" },
  { key: "intermedia", label: "Intermedia", kind: "sala" },
  { key: "superior", label: "Superior", kind: "sala" },
  { key: "universidad", label: "Camino a la Universidad", kind: "sala" },
  { key: "stem", label: "STEM y Juegos", kind: "sala" },
  { key: "ia", label: "Inteligencia Artificial", kind: "sala" },
  { key: "juegos", label: "Juegos", kind: "sala" },
  { key: "recursos", label: "Recursos", kind: "sala" },
  { key: "videoteca", label: "Videoteca", kind: "sala" },
  { key: "deportes", label: "Videoteca Deportes", kind: "sala" },
  { key: "eventos", label: "Eventos", kind: "sala" },
  { key: "adultos", label: "Solicitudes", kind: "sala" },
  { key: "trivia", label: "Trivia de Puerto Rico", kind: "juego" },
  { key: "vf", label: "¿Verdadero o Falso?", kind: "juego" },
  { key: "memoria", label: "Memoria", kind: "juego" },
  { key: "puzzle", label: "Rompecabezas", kind: "juego" },
  { key: "ahorcado", label: "Ahorcado", kind: "juego" },
  { key: "sopa", label: "Sopa de Letras", kind: "juego" },
  { key: "oracion", label: "Ordena la Oración", kind: "juego" },
  { key: "mate", label: "Reto de Matemáticas", kind: "juego" },
  { key: "programacion", label: "Programación (Blockly)", kind: "juego" },
  { key: "phet", label: "Simulaciones interactivas", kind: "juego" },
];

// Antes varias cosas eran "solo web". Ahora la app móvil (KidsIQ) es la
// experiencia principal en teléfono/tablet e incluye juegos y salas; los
// interactivos pesados se filtran por ANCHO de pantalla (desktopOnly), no aquí.
// La bibliotecaria puede apagar algo por plataforma cuando lo necesite.
const WEB_ONLY = new Set<string>([]);

function defaults(): Record<string, Flag> {
  const out: Record<string, Flag> = {};
  for (const it of FLAG_ITEMS) out[it.key] = { on: true, web: true, movil: !WEB_ONLY.has(it.key) };
  return out;
}

const KEY = "ikids.flags";

// Plataforma efectiva. Es "movil" cuando corremos DENTRO de la app nativa KidsIQ
// (la cáscara marca el user-agent con "KidsIQApp" y abre la web con ?app=kidsiq).
// En un navegador normal —incluido un teléfono— es "web". Se cachea: no cambia
// durante la sesión.
let _platform: "web" | "movil" | undefined;
export function currentPlatform(): "web" | "movil" {
  if (_platform) return _platform;
  try {
    const enApp =
      /KidsIQApp/i.test(navigator.userAgent) ||
      /[?&]app=kidsiq\b/i.test(window.location.search);
    _platform = enApp ? "movil" : "web";
  } catch {
    _platform = "web";
  }
  return _platform;
}

export function getFlags(): Record<string, Flag> {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) ?? "null");
    return { ...defaults(), ...(saved ?? {}) };
  } catch {
    return defaults();
  }
}

export function setFlag(key: string, patch: Partial<Flag>): Record<string, Flag> {
  const all = getFlags();
  all[key] = { ...all[key], ...patch };
  localStorage.setItem(KEY, JSON.stringify(all));
  return all;
}

export function resetFlags(): void {
  localStorage.removeItem(KEY);
}

// ¿Disponible en la plataforma actual? Por defecto usa la plataforma detectada
// (web en el navegador, movil dentro de la app KidsIQ).
export function isEnabled(key: string, platform: "web" | "movil" = currentPlatform()): boolean {
  // El estudiante de demostración ve TODO, sin importar los flags del dispositivo.
  if (getSession()?.user === DEMO_STUDENT) return true;
  // Las salas se configuran por residencial en la API. localStorage queda solo
  // para los juegos internos de STEM, que sí son ajustes de esta instalación.
  if (FLAG_ITEMS.find((item) => item.key === key)?.kind === "sala") return true;
  const f = getFlags()[key];
  if (!f) return true;
  return f.on && (platform === "web" ? f.web : f.movil);
}
